import { Activity, AlertTriangleIcon, ClipboardListIcon, ClockIcon, MinusCircleIcon, PauseIcon, Search } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import BulkUpdateTaskTypesDialog from 'components/admin/workflow_tasks/BulkUpdateTaskTypesDialog'
import TaskTypeSummary from 'components/admin/workflow_tasks/TaskTypeSummary'
import AnimateHeight from 'components/reusable/AnimateHeight'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Banner, Button, Select, TextInput } from 'slab'
import { useGetBrokersTasksTaskTypes, useGetBrokersTasksV2Statistics } from 'utils/web_api'

// Now using actual generated hooks and types

// Main loading skeleton component
const WorkflowTasksListViewSkeleton: React.FC = () => (
  <div className='space-y-6 container mx-auto px-4 py-6'>
    {/* Header */}
    <div>
      <h1 className='text-2xl font-bold text-gray-900'>Workflow Tasks</h1>
      <p className='text-gray-600'>Manage V2 workflow tasks and monitor execution</p>
    </div>

    {/* Summary Statistics Skeleton */}
    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4'>
      {Array.from({ length: 7 }).map((_, index) => (
        <StatisticsCardSkeleton key={`stats-${index}`} />
      ))}
    </div>

    {/* Task Types List Skeleton */}
    <div>
      <h2 className='text-lg font-semibold text-gray-900 mb-4'>Task Types</h2>

      {/* Search Bar */}
      <div className='mb-6'>
        <TextInput
          placeholder='Search task types by name...'
          value=''
          onChange={() => {}}
          leadingIcon={Search}
          disabled
        />
      </div>

      {/* Filters */}
      <div className='mb-6'>
        <div className='flex items-center gap-4'>
          <span className='text-sm font-medium text-gray-700'>Filters:</span>
          <div className='flex flex-wrap gap-2'>
            {Array.from({ length: 3 }).map((_, index) => (
              <SkeletonItem key={`filter-${index}`} widthClass='w-20' heightClass='h-8' />
            ))}
          </div>
        </div>
      </div>

      {/* Task Type Cards Skeleton */}
      <div className='grid gap-4' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 24rem), 1fr))' }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <TaskTypeCardSkeleton key={`task-type-${index}`} />
        ))}
      </div>
    </div>
  </div>
)

// Skeleton component for statistics cards
const StatisticsCardSkeleton: React.FC = () => (
  <Card className='bg-white'>
    <CardHeader className='pb-2'>
      <CardTitle className='text-sm font-medium flex items-center gap-2 truncate'>
        <div className='w-4 h-4 bg-gray-200 rounded animate-pulse' />
        <SkeletonItem widthClass='w-20' heightClass='h-4' />
      </CardTitle>
    </CardHeader>
    <CardContent className='pt-0'>
      <SkeletonItem widthClass='w-12' heightClass='h-8' />
    </CardContent>
  </Card>
)

// Skeleton component for task type cards
const TaskTypeCardSkeleton: React.FC = () => (
  <Card className='bg-white'>
    <CardHeader className='pb-4'>
      <div className='flex items-start justify-between'>
        <div className='flex-1'>
          <SkeletonItem widthClass='w-3/4' heightClass='h-6' />
        </div>
        <div className='flex-shrink-0 ml-2'>
          <SkeletonItem widthClass='w-20' heightClass='h-6' />
        </div>
      </div>
    </CardHeader>

    <CardContent className='space-y-4'>
      {/* Task Type Info */}
      <div className='flex items-center gap-2'>
        <SkeletonItem widthClass='w-16' heightClass='h-4' />
        <SkeletonItem widthClass='w-32' heightClass='h-4' />
      </div>

      {/* Task Progress Section */}
      <div className='space-y-3'>
        <SkeletonItem widthClass='w-24' heightClass='h-4' />

        {/* Progress bar skeleton */}
        <div className='space-y-2'>
          <SkeletonItem widthClass='w-full' heightClass='h-2' />
          <div className='flex justify-between items-center'>
            <SkeletonItem widthClass='w-8' heightClass='h-4' />
            <div className='flex items-center gap-3'>
              <SkeletonItem widthClass='w-8' heightClass='h-4' />
              <SkeletonItem widthClass='w-8' heightClass='h-4' />
              <SkeletonItem widthClass='w-8' heightClass='h-4' />
            </div>
          </div>
        </div>
      </div>

      {/* Actions Section */}
      <div className='pt-3 border-t border-gray-100'>
        <SkeletonItem widthClass='w-full' heightClass='h-8' />
      </div>
    </CardContent>
  </Card>
)

const WorkflowTasksListView: React.FC = () => {
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set())
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<Set<string>>(new Set())
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [executionMethodFilter, setExecutionMethodFilter] = useState<string | null>(null)
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false)

  // Calculate date range filters
  const dateFilters = React.useMemo(() => {
    if (dateRange === 'all') {
      return {} // No date filters for "all tasks"
    }
    const days = parseInt(dateRange, 10)
    const afterDate = new Date()
    afterDate.setDate(afterDate.getDate() - days)
    return {
      after_date: afterDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
    }
  }, [dateRange])

  const { data: statistics, isLoading: statsLoading, error: statsError } = useGetBrokersTasksV2Statistics(dateFilters)
  const { data: taskTypes, isLoading: taskTypesLoading, error: taskTypesError } = useGetBrokersTasksTaskTypes(dateFilters)

  const isLoading = statsLoading || taskTypesLoading
  const hasError = statsError || taskTypesError

  // Define available filters
  const filterOptions = [
    { key: 'has_tasks', label: 'Has Tasks' },
    { key: 'on_hold', label: 'On Hold' },
    { key: 'recent_activity', label: 'Recent Activity' },
  ]

  // Category filter options
  const categoryOptions = [
    { key: 'submission', label: 'Submission' },
    { key: 'payment', label: 'Payment' },
    { key: 'autopay', label: 'Autopay' },
  ]

  // Execution method filter options
  const executionMethodOptions = [
    { key: 'automated', label: 'Automated' },
    { key: 'manual', label: 'Manual' },
  ]

  // Toggle filter
  const toggleFilter = (filterKey: string) => {
    const newFilters = new Set(activeFilters)
    if (newFilters.has(filterKey)) {
      newFilters.delete(filterKey)
    } else {
      newFilters.add(filterKey)
    }
    setActiveFilters(newFilters)
  }

  // Toggle category filter
  const toggleCategoryFilter = (category: string) => {
    setCategoryFilter(categoryFilter === category ? null : category)
  }

  // Toggle execution method filter
  const toggleExecutionMethodFilter = (method: string) => {
    setExecutionMethodFilter(executionMethodFilter === method ? null : method)
  }

  // Toggle task type selection
  const toggleTaskTypeSelection = (taskType: string) => {
    const newSelection = new Set(selectedTaskTypes)
    if (newSelection.has(taskType)) {
      newSelection.delete(taskType)
    } else {
      newSelection.add(taskType)
    }
    setSelectedTaskTypes(newSelection)
  }

  // Select all visible task types
  const selectAll = () => {
    const allTaskTypes = new Set(filteredTaskTypes.map(t => t.task_type))
    setSelectedTaskTypes(allTaskTypes)
  }

  // Deselect all task types
  const deselectAll = () => {
    setSelectedTaskTypes(new Set())
  }

  // Filter task types based on search term and filters
  const filteredTaskTypes = taskTypes?.filter(taskType => {
    // Search filter
    const matchesSearch = taskType.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         taskType.task_type.toLowerCase().includes(searchTerm.toLowerCase())

    // Apply filters
    let matchesFilters = true

    if (activeFilters.has('has_tasks')) {
      matchesFilters = matchesFilters && taskType.total_tasks > 0
    }

    if (activeFilters.has('on_hold')) {
      matchesFilters = matchesFilters && taskType.has_active_hold
    }

    if (activeFilters.has('recent_activity')) {
      matchesFilters = matchesFilters && (taskType.recent_activity as any)?.tasks_last_24h > 0
    }

    // Category filter - check if task type has any of the specified categories
    if (categoryFilter) {
      const taskCategories = (taskType as any).categories || []
      matchesFilters = matchesFilters && taskCategories.includes(categoryFilter)
    }

    // Execution method filter
    if (executionMethodFilter) {
      matchesFilters = matchesFilters && taskType.default_execution_method === executionMethodFilter
    }

    return matchesSearch && matchesFilters
  }) || []

  // Navigation function for task types
  const onNavigateToTaskType = (taskType: string) => {
    const encodedTaskType = encodeURIComponent(taskType)
    navigate(`/admin/tasks/v2/${encodedTaskType}`)
  }

  if (isLoading) {
    return <WorkflowTasksListViewSkeleton />
  }

  if (hasError) {
    return (
      <Banner type='critical' title='Unable to get Workflow Tasks'>
        {(() => {
          if (statsError && typeof statsError === 'object' && 'message' in statsError) {
            return String((statsError as any).message)
          }
          if (taskTypesError && typeof taskTypesError === 'object' && 'message' in taskTypesError) {
            return String((taskTypesError as any).message)
          }
          return 'An unknown error occurred'
        })()}
      </Banner>
    )
  }

  // Get statistics values from API
  const totalTasks = statistics?.total_tasks || 0
  const pendingTasks = (statistics?.task_status as any)?.pending || 0
  const processingTasks = (statistics?.task_status as any)?.processing || 0
  const failedTasks = (statistics?.task_status as any)?.failed || 0
  const completedTasks = (statistics?.task_status as any)?.completed || 0
  const excludedTasks = (statistics?.task_status as any)?.excluded || 0
  const canceledTasks = (statistics?.task_status as any)?.canceled || 0
  const v2HoldsCount = statistics?.v2_holds_count || 0

  // Calculate total excluding canceled tasks
  const activeTotalTasks = totalTasks - canceledTasks

  return (
    <div className='space-y-6 container mx-auto px-4 py-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Workflow Tasks</h1>
          <p className='text-gray-600'>Manage V2 workflow tasks and monitor execution</p>
        </div>
        <div className='flex items-center gap-2'>
          <label htmlFor='date-range-select' className='text-sm font-medium text-gray-700'>
            Date Range:
          </label>
          <Select
            id='date-range-select'
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className='w-36'
          >
            <option value='7'>Last 7 days</option>
            <option value='14'>Last 14 days</option>
            <option value='30'>Last 30 days</option>
            <option value='all'>All tasks</option>
          </Select>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4'>
        <Card className='bg-white border border-purple-400'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-purple-400 flex items-center gap-2 truncate'>
              <Activity className='w-4 h-4 flex-shrink-0' />
              Total Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-purple-600'>{activeTotalTasks}</div>
          </CardContent>
        </Card>

        <Card className='bg-white border border-green-300'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-green-400 flex items-center gap-2 truncate'>
              <ClipboardListIcon className='w-4 h-4 flex-shrink-0' />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-green-500'>{completedTasks}</div>
          </CardContent>
        </Card>

        <Card className='bg-white border border-yellow-300'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-yellow-400 flex items-center gap-2 truncate'>
              <ClockIcon className='w-4 h-4 flex-shrink-0' />
              Pending Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-yellow-500'>{pendingTasks}</div>
          </CardContent>
        </Card>

        <Card className='bg-white border border-blue-300'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-blue-400 flex items-center gap-2 truncate'>
              <Activity className='w-4 h-4 flex-shrink-0' />
              Processing
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-blue-500'>{processingTasks}</div>
          </CardContent>
        </Card>

        <Card className='bg-white border border-orange-300'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-orange-400 flex items-center gap-2 truncate'>
              <AlertTriangleIcon className='w-4 h-4 flex-shrink-0' />
              Failed Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-orange-500'>{failedTasks}</div>
          </CardContent>
        </Card>

        <Card className='bg-white border border-gray-300'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-gray-400 flex items-center gap-2 truncate'>
              <MinusCircleIcon className='w-4 h-4 flex-shrink-0' />
              Excluded
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-gray-500'>{excludedTasks}</div>
          </CardContent>
        </Card>

        <Card className='bg-white border border-red-300'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-red-400 flex items-center gap-2 truncate'>
              <PauseIcon className='w-4 h-4 flex-shrink-0' />
              V2 Holds
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-red-700'>{v2HoldsCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Task Types List */}
      <div>
        <h2 className='text-lg font-semibold text-gray-900 mb-4'>Task Types</h2>

        {/* Search Bar */}
        <div className='mb-6'>
          <TextInput
            placeholder='Search task types by name...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={Search}
          />
        </div>

        {/* Filters */}
        <AnimateHeight>
          <div className='mb-6'>
            <div className='flex items-center gap-4 flex-wrap'>
              <span className='text-sm font-medium text-gray-700'>Filters:</span>
              <div className='flex flex-wrap gap-2'>
                {filterOptions.map(({ key, label }) => (
                  <Button
                    key={key}
                    variant={activeFilters.has(key) ? 'tertiary' : 'tertiary-deemphasized'}
                    size='small'
                    onClick={() => toggleFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
                {categoryOptions.map(({ key, label }) => (
                  <Button
                    key={key}
                    variant={categoryFilter === key ? 'tertiary' : 'tertiary-deemphasized'}
                    size='small'
                    onClick={() => toggleCategoryFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
                {executionMethodOptions.map(({ key, label }) => (
                  <Button
                    key={key}
                    variant={executionMethodFilter === key ? 'tertiary' : 'tertiary-deemphasized'}
                    size='small'
                    onClick={() => toggleExecutionMethodFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            <AnimateOpacity isOpen={activeFilters.size > 0 || categoryFilter !== null || executionMethodFilter !== null}>
              <div className='my-3 text-sm text-gray-600'>
                {activeFilters.size > 0 && (
                  <>
                    {activeFilters.size} filter{activeFilters.size !== 1 ? 's' : ''} active
                  </>
                )}
                {(activeFilters.size > 0 && categoryFilter) && ' • '}
                {categoryFilter && (
                  <>
                    Category: {categoryOptions.find(c => c.key === categoryFilter)?.label}
                  </>
                )}
                {((activeFilters.size > 0 || categoryFilter) && executionMethodFilter) && ' • '}
                {executionMethodFilter && (
                  <>
                    Method: {executionMethodOptions.find(m => m.key === executionMethodFilter)?.label}
                  </>
                )}
                <button
                  onClick={() => {
                    setActiveFilters(new Set())
                    setCategoryFilter(null)
                    setExecutionMethodFilter(null)
                  }}
                  className='ml-2 text-blue-600 hover:text-blue-800 underline'
                >
                  Clear all
                </button>
              </div>
            </AnimateOpacity>
          </div>
        </AnimateHeight>

        {/* Selection Controls */}
        {filteredTaskTypes.length > 0 && (
          <div className='mb-4 flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <Button
                variant='tertiary'
                size='small'
                onClick={selectAll}
              >
                Select All ({filteredTaskTypes.length})
              </Button>
              <Button
                variant='tertiary'
                size='small'
                onClick={deselectAll}
                disabled={selectedTaskTypes.size === 0}
              >
                Deselect All
              </Button>
              {selectedTaskTypes.size > 0 && (
                <span className='text-sm text-gray-600'>
                  {selectedTaskTypes.size} selected
                </span>
              )}
            </div>

            {selectedTaskTypes.size > 0 && (
              <div className='flex items-center gap-2'>
                <Button
                  variant='secondary'
                  size='small'
                  onClick={() => setIsBulkUpdateDialogOpen(true)}
                >
                  Bulk Update Holds
                </Button>
              </div>
            )}
          </div>
        )}

        <AnimateOpacity
          key={filteredTaskTypes.map(t => t.task_type).sort().join(',')}
          isOpen
        >
          {filteredTaskTypes.length > 0
            ? (
              <div className='p-2'>
                <div className='grid gap-4' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 24rem), 1fr))' }}>
                  {filteredTaskTypes.map((taskType) => (
                    <TaskTypeSummary
                      key={taskType.task_type}
                      taskType={taskType as any}
                      onNavigateToTaskType={onNavigateToTaskType}
                      isSelected={selectedTaskTypes.has(taskType.task_type)}
                      onToggleSelect={() => toggleTaskTypeSelection(taskType.task_type)}
                    />
                  ))}
                </div>
              </div>
              )
            : (
              <div className='text-center py-8'>
                <p className='text-gray-500'>
                  {searchTerm || activeFilters.size > 0 || categoryFilter || executionMethodFilter
                    ? `No task types found${searchTerm ? ` matching "${searchTerm}"` : ''}${activeFilters.size > 0 || categoryFilter || executionMethodFilter ? ' with selected filters' : ''}`
                    : 'No task types found'}
                </p>
              </div>
              )}
        </AnimateOpacity>
      </div>

      {/* Bulk Update Dialog */}
      <BulkUpdateTaskTypesDialog
        isOpen={isBulkUpdateDialogOpen}
        onClose={() => setIsBulkUpdateDialogOpen(false)}
        selectedTaskTypes={Array.from(selectedTaskTypes)}
        onSuccess={() => {
          // Clear selection after successful update
          setSelectedTaskTypes(new Set())
        }}
      />
    </div>
  )
}

export default WorkflowTasksListView
